/**
 * Event date/time formatting, pinned to the parish's timezone.
 *
 * Events happen at a fixed local wall-clock time in mainland Portugal, so every
 * visitor — wherever they are browsing from — should see that same local time.
 * The backend serializes `startsAt`/`endsAt` as offset-aware ISO strings
 * (e.g. `2026-07-12T21:30:00+01:00`); these helpers render them in Lisbon time
 * rather than the viewer's browser zone.
 */

export const PARISH_TIME_ZONE = 'Europe/Lisbon';

/** Day of month, zero-padded (e.g. "12"). */
export function eventDay(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-PT', {
    day: '2-digit',
    timeZone: PARISH_TIME_ZONE,
  });
}

/** Abbreviated month, lowercase, no trailing dot (e.g. "jul"). Callers uppercase as needed. */
export function eventMonth(iso: string): string {
  return new Date(iso)
    .toLocaleDateString('pt-PT', { month: 'short', timeZone: PARISH_TIME_ZONE })
    .replace('.', '');
}

/** Time of day, 24h (e.g. "21:30"). */
export function eventTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-PT', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: PARISH_TIME_ZONE,
  });
}

/** Calendar year/month/day (month 0-indexed) as seen in the parish timezone. */
export function eventDateParts(iso: string): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: PARISH_TIME_ZONE,
  }).formatToParts(new Date(iso));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { year: get('year'), month: get('month') - 1, day: get('day') };
}
